/* ==========================================================================
   Joseph A. — theme behaviour
   Ports the interaction patterns documented in the Style Guide.

   Layout responds to a single breakpoint, driven by matchMedia rather than
   pure CSS because the breakpoint is a theme setting. The `is-mobile` /
   `is-desktop` classes are set on <html> by an inline head script so the
   first paint is already correct; this file keeps them in sync on resize
   and drives everything else.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function on(el, evt, fn, opts) {
    if (el) el.addEventListener(evt, fn, opts);
  }

  /* -- Breakpoint ---------------------------------------------------------- */

  var mq = root.__jaMobileQuery ||
    window.matchMedia('(max-width: ' + (parseInt(document.body.dataset.mobileBreakpoint, 10) || 860) + 'px)');

  function isMobile() {
    return mq.matches;
  }

  function onBreakpointChange(fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else mq.addListener(fn);
  }

  /* The class itself is owned by the inline head script; this hook is for
     behaviour that must react to the breakpoint, such as closing the nav. */

  /* -- Image load-in ------------------------------------------------------- */
  /* Images start at opacity 0 and fade in once decoded. Images already
     complete at parse time (cached) are revealed immediately so they never
     sit invisible waiting for a load event that already fired. */

  function revealImages(scope) {
    var imgs = (scope || document).querySelectorAll('img.js-fade:not([data-fade-bound])');
    Array.prototype.forEach.call(imgs, function (img) {
      img.setAttribute('data-fade-bound', '');
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        on(img, 'load', function () { img.classList.add('is-loaded'); });
        on(img, 'error', function () { img.classList.add('is-loaded'); });
      }
    });
  }

  /* -- Header: scroll condense --------------------------------------------- */

  /* `header` is the shell element carrying data-header, not the <header> bar:
     state classes live on the shell so they can reach the backdrop, which is a
     sibling of the bar rather than a child. */
  function initHeader(header) {
    if (!header || header.dataset.bound) return;
    header.dataset.bound = '1';

    var threshold = parseInt(header.dataset.scrollThreshold, 10) || 60;
    var scrolled = null;

    function sync() {
      var next = (window.scrollY || 0) > threshold;
      if (next !== scrolled) {
        scrolled = next;
        header.classList.toggle('is-scrolled', next);
      }
    }

    sync();
    on(window, 'scroll', sync, { passive: true });

    /* Dropdowns — one per nav item that has children. Hover on desktop,
       click for touch and keyboard. */
    Array.prototype.forEach.call(header.querySelectorAll('[data-nav-group]'), function (group) {
      var trigger = group.querySelector('[data-nav-group-trigger]');

      function setOpen(open) {
        group.classList.toggle('is-open', open);
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      on(group, 'mouseenter', function () { if (!isMobile()) setOpen(true); });
      on(group, 'mouseleave', function () { if (!isMobile()) setOpen(false); });
      on(group, 'focusout', function (e) {
        if (!group.contains(e.relatedTarget)) setOpen(false);
      });
      on(trigger, 'click', function (e) {
        e.preventDefault();
        setOpen(!group.classList.contains('is-open'));
      });
      on(group, 'keydown', function (e) {
        if (e.key === 'Escape' && group.classList.contains('is-open')) {
          setOpen(false);
          if (trigger) trigger.focus();
        }
      });
    });

    /* Search — swaps the header row for a full-width input panel. */
    var search = header.querySelector('[data-search-panel]');
    if (search) {
      var searchInput = search.querySelector('input');
      var catcher = header.querySelector('[data-search-catcher]');

      function setSearch(open) {
        header.classList.toggle('search-open', open);
        search.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (open && searchInput) searchInput.focus();
      }

      Array.prototype.forEach.call(header.querySelectorAll('[data-search-toggle]'), function (btn) {
        on(btn, 'click', function () { setSearch(!header.classList.contains('search-open')); });
      });
      on(catcher, 'click', function () { setSearch(false); });
      on(header, 'keydown', function (e) {
        if (e.key === 'Escape' && header.classList.contains('search-open')) setSearch(false);
      });
    }

    /* Mobile nav — hamburger morphs to X, panel expands. */
    var burger = header.querySelector('[data-nav-toggle]');
    var panel = header.querySelector('[data-nav-panel]');
    if (burger && panel) {
      function setNav(open) {
        header.classList.toggle('nav-open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      }

      on(burger, 'click', function () { setNav(!header.classList.contains('nav-open')); });
      on(header.querySelector('[data-nav-backdrop]'), 'click', function () { setNav(false); });
      Array.prototype.forEach.call(panel.querySelectorAll('a'), function (a) {
        on(a, 'click', function () { setNav(false); });
      });
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && header.classList.contains('nav-open')) setNav(false);
      });
      onBreakpointChange(function () { setNav(false); });
    }
  }

  /* -- Parallax ------------------------------------------------------------ */
  /* Elements carrying data-parallax-range drift vertically as they cross the
     viewport. One shared rAF loop for all of them; it parks itself whenever
     nothing has moved so an idle page costs nothing. */

  var parallax = null;

  function initParallax() {
    if (document.body.dataset.parallax !== 'enabled' || reduceMotion.matches) return;

    /* Re-entrant: the theme editor reloads sections, which can add or remove
       parallax elements. Re-scan rather than starting a second rAF loop. */
    if (parallax) {
      parallax.collect();
      parallax.wake();
      return;
    }

    var items = [];
    var running = false;
    var dirty = true;

    function collect() {
      items = Array.prototype.map.call(
        document.querySelectorAll('[data-parallax-range]'),
        function (el) {
          return { el: el, range: parseFloat(el.dataset.parallaxRange) || 0, y: null };
        }
      );
    }

    function offsetFor(item) {
      var r = item.el.getBoundingClientRect();
      var vh = window.innerHeight || 800;
      var progress = Math.min(Math.max((vh - r.top) / (vh + r.height), 0), 1);
      return (progress - 0.5) * item.range;
    }

    function frame() {
      var moved = false;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var next = offsetFor(item);
        if (item.y === null || Math.abs(next - item.y) > 0.3) {
          item.y = next;
          item.el.style.transform = 'translate3d(0, ' + next.toFixed(2) + 'px, 0)';
          moved = true;
        }
      }

      if (moved || dirty) {
        dirty = false;
        requestAnimationFrame(frame);
      } else {
        running = false;
      }
    }

    function wake() {
      dirty = true;
      if (!running) {
        running = true;
        requestAnimationFrame(frame);
      }
    }

    parallax = { collect: collect, wake: wake };
    collect();
    on(window, 'scroll', wake, { passive: true });
    on(window, 'resize', function () { collect(); wake(); }, { passive: true });
    wake();
  }

  /* -- Newsletter floating label ------------------------------------------- */

  function initNewsletter(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-float-field]:not([data-float-bound])'),
      function (field) {
        field.setAttribute('data-float-bound', '');
        var input = field.querySelector('input');
        if (!input) return;
        on(input, 'focus', function () { field.classList.add('is-active'); });
        on(input, 'blur', function () {
          if (!input.value) field.classList.remove('is-active');
        });
        if (input.value) field.classList.add('is-active');
      }
    );
  }

  /* -- Price range slider --------------------------------------------------- */
  /* Enhancement only: the number inputs carry the filter params and submit on
     their own. The two range inputs are unnamed and just drive those. */

  function initPriceRange(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-price-range]:not([data-price-bound])'),
      function (root) {
        root.setAttribute('data-price-bound', '');

        var ceiling = parseFloat(root.dataset.ceiling) || 0;
        if (!ceiling) return;

        var fromNum = root.querySelector('[data-price-from]');
        var toNum = root.querySelector('[data-price-to]');
        var fromRange = root.querySelector('[data-price-range-from]');
        var toRange = root.querySelector('[data-price-range-to]');
        var fill = root.querySelector('[data-price-fill]');
        if (!fromNum || !toNum || !fromRange || !toRange) return;

        function clamp(n) {
          return Math.min(Math.max(isNaN(n) ? 0 : n, 0), ceiling);
        }

        function paint(from, to) {
          if (!fill) return;
          fill.style.left = (from / ceiling) * 100 + '%';
          fill.style.right = 100 - (to / ceiling) * 100 + '%';
        }

        /* Empty means "unbounded", which is not the same as 0 or the ceiling —
           only write a number back when the handle actually constrains. */
        function sync(from, to, writeNumbers) {
          fromRange.value = from;
          toRange.value = to;
          if (writeNumbers) {
            fromNum.value = from > 0 ? from : '';
            toNum.value = to < ceiling ? to : '';
          }
          paint(from, to);
        }

        function readNumbers() {
          var from = fromNum.value === '' ? 0 : clamp(parseFloat(fromNum.value));
          var to = toNum.value === '' ? ceiling : clamp(parseFloat(toNum.value));
          if (from > to) { var t = from; from = to; to = t; }
          return [from, to];
        }

        var initial = readNumbers();
        sync(initial[0], initial[1], false);

        on(fromRange, 'input', function () {
          var from = clamp(parseFloat(fromRange.value));
          var to = clamp(parseFloat(toRange.value));
          if (from > to) from = to;
          sync(from, to, true);
        });

        on(toRange, 'input', function () {
          var from = clamp(parseFloat(fromRange.value));
          var to = clamp(parseFloat(toRange.value));
          if (to < from) to = from;
          sync(from, to, true);
        });

        [fromNum, toNum].forEach(function (input) {
          on(input, 'input', function () {
            var v = readNumbers();
            sync(v[0], v[1], false);
          });
        });

        /* Committing the handle is the moment to filter, not every pixel of drag. */
        [fromRange, toRange].forEach(function (input) {
          on(input, 'change', function () {
            if (!isMobile()) submitFilters(root);
          });
        });
        [fromNum, toNum].forEach(function (input) {
          on(input, 'change', function () {
            if (!isMobile()) submitFilters(root);
          });
        });
      }
    );
  }

  function submitFilters(el) {
    var form = el.closest('[data-filter-form]');
    if (form) form.submit();
  }

  /* -- Collection: filter panel and sort ----------------------------------- */

  function initCollection(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-collection]:not([data-collection-bound])'),
      function (root) {
        root.setAttribute('data-collection-bound', '');

        var toggles = root.querySelectorAll('[data-filter-toggle]');
        var trigger = root.querySelector('[data-filter-toggle][aria-controls]');

        function setOpen(open) {
          root.classList.toggle('filters-open', open);
          if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        }

        Array.prototype.forEach.call(toggles, function (btn) {
          on(btn, 'click', function () {
            setOpen(!root.classList.contains('filters-open'));
          });
        });

        on(document, 'keydown', function (e) {
          if (e.key === 'Escape' && root.classList.contains('filters-open')) setOpen(false);
        });

        /* Open by default on desktop when filters are already applied, so the
           active state is visible rather than hidden behind the toggle. */
        if (!isMobile() && root.querySelector('.collection__chip')) setOpen(true);

        /* Submitting on change saves a click. The Apply button stays for
           no-JS and for mobile, where changing five things then submitting
           once beats five page loads. */
        var sortForm = root.querySelector('[data-sort-form]');
        if (sortForm) {
          on(sortForm.querySelector('select'), 'change', function () { sortForm.submit(); });
        }

        var filterForm = root.querySelector('[data-filter-form]');
        if (filterForm) {
          Array.prototype.forEach.call(filterForm.querySelectorAll('input[type="checkbox"]'), function (input) {
            on(input, 'change', function () {
              if (!isMobile()) filterForm.submit();
            });
          });
        }
      }
    );
  }

  /* -- Product: variant selection -------------------------------------------- */

  function initProduct(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-product]:not([data-product-bound])'),
      function (root) {
        root.setAttribute('data-product-bound', '');

        var picker = root.querySelector('[data-variant-picker]');
        if (!picker) return;

        var dataEl = root.querySelector('[data-variant-data]');
        var variants;
        try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
        if (!variants || !variants.length) return;

        var idInput = picker.querySelector('[data-variant-id]');
        var priceCurrent = root.querySelector('[data-price-current]');
        var priceCompare = root.querySelector('[data-price-compare]');
        var submit = root.querySelector('[data-add-to-cart]');
        var submitText = root.querySelector('[data-add-to-cart-text]');
        var skuEl = root.querySelector('[data-sku]');
        var mediaWrap = root.querySelector('[data-product-media]');
        var inputs = picker.querySelectorAll('.variant-option__input');

        function selected() {
          var values = [];
          Array.prototype.forEach.call(inputs, function (input) {
            if (input.checked) values[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
          });
          return values;
        }

        function match(values) {
          for (var i = 0; i < variants.length; i++) {
            var v = variants[i], ok = true;
            for (var j = 0; j < values.length; j++) {
              if (v.options[j] !== values[j]) { ok = false; break; }
            }
            if (ok) return v;
          }
          return null;
        }

        /* Grey out values that cannot be reached from the current selection,
           holding the option being considered free so the shopper can always
           pivot on it. */
        function markUnavailable(values) {
          Array.prototype.forEach.call(inputs, function (input) {
            var pos = parseInt(input.dataset.optionPosition, 10) - 1;
            var probe = values.slice();
            probe[pos] = input.value;
            var found = null;
            for (var i = 0; i < variants.length; i++) {
              var v = variants[i], ok = true;
              for (var j = 0; j < probe.length; j++) {
                if (probe[j] !== undefined && v.options[j] !== probe[j]) { ok = false; break; }
              }
              if (ok) { found = v; break; }
            }
            input.disabled = !found;
          });
        }

        var thumbWrap = root.querySelector('[data-pdp-thumbs]');

        function setActiveThumb(mediaId) {
          if (!thumbWrap) return;
          Array.prototype.forEach.call(thumbWrap.children, function (btn) {
            btn.classList.toggle('is-active', btn.dataset.thumb === String(mediaId));
          });
        }

        var mediaItems = mediaWrap ? Array.prototype.slice.call(mediaWrap.children) : [];
        var thumbs = thumbWrap ? Array.prototype.slice.call(thumbWrap.children) : [];

        function selectedColor() {
          var input = picker.querySelector('[data-swatch-option] .variant-option__input:checked');
          return input ? input.value.trim().toLowerCase() : '';
        }

        /* Show only the chosen colour's photography. Media belonging to no
           colour stay put, so shared detail and lifestyle shots are never lost.
           Returns the first shot of that colour — where the gallery lands when
           the variant carries no image of its own. */
        function filterMediaByColor() {
          var color = selectedColor();
          var tagged = false;
          if (color) {
            for (var i = 0; i < mediaItems.length; i++) {
              if ((mediaItems[i].dataset.mediaColor || '') === color) { tagged = true; break; }
            }
          }
          if (!tagged) {
            mediaItems.forEach(function (item, i) {
              item.hidden = false;
              if (thumbs[i]) thumbs[i].hidden = false;
            });
            return null;
          }

          var first = null;
          mediaItems.forEach(function (item, i) {
            var own = item.dataset.mediaColor || '';
            var show = own === '' || own === color;
            item.hidden = !show;
            if (thumbs[i]) thumbs[i].hidden = !show;
            if (show && !first && own === color) first = item;
          });
          return first;
        }

        function showMedia(mediaId, noScroll) {
          if (!mediaWrap) return;
          var firstOfColor = filterMediaByColor();
          var item = mediaId ? mediaWrap.querySelector('[data-media-id="' + mediaId + '"]') : null;
          if (item && item.hidden) item = null;
          if (!item) item = firstOfColor;
          if (!item) return;
          mediaId = item.dataset.mediaId;

          if (!noScroll) {
            /* Whether the strip scrolls, rather than which breakpoint we are
               at: the gallery is a horizontal strip on desktop too now, and
               the rail should move it sideways instead of yanking the page. */
            if (mediaWrap.scrollWidth - mediaWrap.clientWidth > 1) {
              mediaWrap.scrollTo({ left: item.offsetLeft - mediaWrap.offsetLeft, behavior: 'smooth' });
            } else {
              var rect = item.getBoundingClientRect();
              var visible = rect.top < window.innerHeight * 0.9 && rect.bottom > window.innerHeight * 0.1;
              if (!visible) item.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }

          setActiveThumb(mediaId);
        }

        if (thumbWrap) {
          Array.prototype.forEach.call(thumbWrap.children, function (btn) {
            on(btn, 'click', function () { showMedia(btn.dataset.thumb); });
          });

          /* Swiping the carousel should move the marker too, so the two never
             disagree about which image is showing. */
          if ('IntersectionObserver' in window && mediaWrap) {
            var io = new IntersectionObserver(function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) setActiveThumb(entry.target.dataset.mediaId);
              });
            }, { root: mediaWrap, threshold: 0.6 });
            Array.prototype.forEach.call(mediaWrap.children, function (item) { io.observe(item); });
          }
        }

        function update() {
          var values = selected();
          var variant = match(values);

          Array.prototype.forEach.call(picker.querySelectorAll('[data-option-display]'), function (el) {
            var i = parseInt(el.dataset.optionDisplay, 10);
            if (values[i]) el.textContent = values[i];
          });

          markUnavailable(values);

          if (!variant) {
            if (idInput) idInput.value = '';
            if (submit) submit.disabled = true;
            if (submitText) submitText.textContent = submit.dataset.unavailableText || submitText.textContent;
            return;
          }

          if (idInput) idInput.value = variant.id;
          if (priceCurrent) priceCurrent.textContent = variant.price;
          if (priceCompare) {
            priceCompare.textContent = variant.compareAt || '';
            priceCompare.hidden = !variant.compareAt;
          }
          if (submit) submit.disabled = !variant.available;
          if (submitText) {
            submitText.textContent = variant.available
              ? (submit.dataset.addText || submitText.textContent)
              : (submit.dataset.soldOutText || submitText.textContent);
          }
          if (skuEl) skuEl.textContent = variant.sku || '';

          showMedia(variant.mediaId);

          /* Keep the URL shareable without adding a history entry per click. */
          if (window.history && history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            history.replaceState({}, '', url.toString());
          }
        }

        Array.prototype.forEach.call(inputs, function (input) {
          on(input, 'change', update);
        });

        markUnavailable(selected());

        /* First paint: apply the colour filter and point the marker at whatever
           survives it, without scrolling the page the shopper just landed on. */
        var initial = match(selected());
        showMedia(initial ? initial.mediaId : null, true);
      }
    );
  }

  /* -- Quantity stepper ------------------------------------------------------ */

  function initQuantity(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-qty]:not([data-qty-bound])'),
      function (root) {
        root.setAttribute('data-qty-bound', '');
        var input = root.querySelector('[data-qty-input]');
        if (!input) return;

        var min = parseInt(input.getAttribute('min'), 10);
        if (isNaN(min)) min = 1;

        function value() {
          var n = parseInt(input.value, 10);
          return isNaN(n) ? min : n;
        }

        function sync() {
          var n = Math.max(value(), min);
          input.value = n;
          var down = root.querySelector('[data-qty-step="-1"]');
          if (down) down.disabled = n <= min;
        }

        Array.prototype.forEach.call(root.querySelectorAll('[data-qty-step]'), function (btn) {
          on(btn, 'click', function () {
            input.value = Math.max(value() + parseInt(btn.dataset.qtyStep, 10), min);
            sync();
            /* Let anything listening on the field react as if it were typed. */
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        on(input, 'change', sync);
        on(input, 'blur', sync);
        sync();
      }
    );
  }

  /* -- Cart drawer ----------------------------------------------------------- */
  /* Mutations go through the AJAX Cart API, then the drawer is re-rendered
     server-side via the Section Rendering API. That keeps money formatting,
     translations and markup in Liquid instead of rebuilt by hand in JS. */

  var rootUrl = (document.body.dataset.rootUrl || '/').replace(/\/$/, '') + '/';

  function cartDrawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function openCart() {
    var el = cartDrawer();
    if (!el) return;
    el.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    var panel = el.querySelector('[data-cart-panel]');
    if (panel) panel.focus();
  }

  function closeCart() {
    var el = cartDrawer();
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.style.overflow = '';
  }

  function setBusy(busy) {
    var el = cartDrawer();
    if (el) el.classList.toggle('is-busy', busy);
  }

  function refreshCart() {
    return fetch(rootUrl + '?sections=cart-drawer', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer]');
        var current = cartDrawer();
        if (!fresh || !current) return;

        /* Swap what is inside the panel, never the panel itself. Replacing the
           node would destroy the element the slide transitions on, and a newly
           inserted node has no previous style to animate from — it would snap
           open instead of sliding. */
        var currentPanel = current.querySelector('[data-cart-panel]');
        var freshPanel = fresh.querySelector('[data-cart-panel]');
        if (currentPanel && freshPanel) {
          currentPanel.innerHTML = freshPanel.innerHTML;
        } else {
          var wasOpen = current.classList.contains('is-open');
          current.innerHTML = fresh.innerHTML;
          if (wasOpen) current.classList.add('is-open');
        }

        var count = fresh.querySelector('[data-cart-count]');
        if (count) {
          var n = (count.textContent.match(/\d+/) || ['0'])[0];
          Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count-value]'), function (el) {
            el.textContent = n;
          });
        }
        bindCart();
      });
  }

  function changeLine(line, quantity) {
    setBusy(true);
    return fetch(rootUrl + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(refreshCart)
      .catch(function () {})
      .then(function () { setBusy(false); });
  }

  function bindCart() {
    var el = cartDrawer();
    if (!el) return;

    Array.prototype.forEach.call(el.querySelectorAll('[data-cart-close]'), function (btn) {
      on(btn, 'click', closeCart);
    });

    Array.prototype.forEach.call(el.querySelectorAll('.cart-item'), function (row) {
      var line = parseInt(row.dataset.line, 10);
      var input = row.querySelector('[data-cart-qty]');

      Array.prototype.forEach.call(row.querySelectorAll('[data-cart-step]'), function (btn) {
        on(btn, 'click', function () {
          var next = Math.max(parseInt(input.value, 10) + parseInt(btn.dataset.cartStep, 10), 0);
          changeLine(line, next);
        });
      });

      on(input, 'change', function () {
        var n = parseInt(input.value, 10);
        changeLine(line, isNaN(n) || n < 0 ? 0 : n);
      });

      on(row.querySelector('[data-cart-remove]'), 'click', function () {
        changeLine(line, 0);
      });
    });
  }

  function initCart() {
    bindCart();

    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-toggle]'), function (link) {
      on(link, 'click', function (e) {
        if (!cartDrawer()) return;
        e.preventDefault();
        openCart();
      });
    });

    on(document, 'keydown', function (e) {
      var el = cartDrawer();
      if (e.key === 'Escape' && el && el.classList.contains('is-open')) closeCart();
    });

    /* Add to cart posts through the API so the page never navigates away. */
    Array.prototype.forEach.call(document.querySelectorAll('form.pdp__form'), function (form) {
      on(form, 'submit', function (e) {
        if (!cartDrawer()) return;
        e.preventDefault();
        var button = form.querySelector('[data-add-to-cart]');
        if (button) button.disabled = true;

        /* Slide in on the click rather than after two round trips, and show the
           busy state while the line is added and the panel refilled. */
        openCart();
        setBusy(true);

        fetch(rootUrl + 'cart/add.js', { method: 'POST', body: new FormData(form) })
          .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.body.description || 'add failed');
            return refreshCart();
          })
          .catch(function () {
            /* Fall back to the real form post rather than failing silently. */
            closeCart();
            form.submit();
          })
          .then(function () {
            setBusy(false);
            if (button) button.disabled = false;
          });
      });
    });
  }

  /* -- Product gallery: fit to screen, and the lightbox --------------------- */

  function initProductGallery(scope) {
    var section = (scope || document).querySelector('[data-product]');
    if (!section || section.dataset.galleryBound) return;
    section.dataset.galleryBound = '1';

    var wrap = section.querySelector('[data-product-media]');
    if (!wrap) return;

    /* -- Size the photograph from the viewport ------------------------------
       Its height is set so roughly this much of it clears the fold; the width
       follows in CSS from the configured crop. Measured rather than written
       as a CSS constant because the announcement bar is optional, the header
       height is a theme setting, and the breadcrumb wraps on narrow windows.

       The offset is the gallery's distance from the top of the document,
       which nothing below it can change, so re-running this never drifts.
       No mobile branch: the is-mobile rules override both the height and the
       column it sizes. */
    var EXPOSED = 0.9;

    var fit = function () {
      var offset = wrap.getBoundingClientRect().top + window.pageYOffset;
      var h = (window.innerHeight - offset) / EXPOSED;
      section.style.setProperty(
        '--pdp-main-height',
        Math.round(Math.max(520, Math.min(1040, h))) + 'px'
      );
    };

    fit();
    var pending;
    on(window, 'resize', function () {
      clearTimeout(pending);
      pending = setTimeout(fit, 120);
    });

    /* -- Lightbox ---------------------------------------------------------- */

    var lightbox = section.querySelector('[data-lightbox]');
    if (!lightbox) return;

    var image = lightbox.querySelector('[data-lightbox-image]');
    var indexEl = lightbox.querySelector('[data-lightbox-index]');
    var totalEl = lightbox.querySelector('[data-lightbox-total]');
    var prevBtn = lightbox.querySelector('[data-lightbox-prev]');
    var nextBtn = lightbox.querySelector('[data-lightbox-next]');
    var closeBtn = lightbox.querySelector('.pdp-lightbox__close');
    var current = 0;
    var lastFocus = null;

    /* Only the frames the colour filter has left on screen, so the arrows
       walk exactly the set of photographs the gallery is showing. */
    function shots() {
      return Array.prototype.filter.call(
        wrap.querySelectorAll('.pdp__media-item[data-zoom-src]'),
        function (el) { return !el.hidden; }
      );
    }

    function show(i) {
      var list = shots();
      if (!list.length) return;
      current = (i + list.length) % list.length;
      var item = list[current];
      image.src = item.dataset.zoomSrc;
      image.alt = item.dataset.zoomAlt || '';
      if (item.dataset.zoomWidth) image.setAttribute('width', item.dataset.zoomWidth);
      if (item.dataset.zoomHeight) image.setAttribute('height', item.dataset.zoomHeight);
      if (indexEl) indexEl.textContent = current + 1;
      if (totalEl) totalEl.textContent = list.length;
      var alone = list.length < 2;
      if (prevBtn) prevBtn.hidden = alone;
      if (nextBtn) nextBtn.hidden = alone;
      lightbox.classList.remove('is-zoomed');
    }

    function open(item) {
      var i = shots().indexOf(item);
      show(i < 0 ? 0 : i);
      lastFocus = document.activeElement;
      lightbox.hidden = false;
      root.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      lightbox.hidden = true;
      root.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    Array.prototype.forEach.call(wrap.querySelectorAll('[data-zoom]'), function (btn) {
      function frame() {
        var item = btn.parentNode;
        while (item && !item.dataset.zoomSrc) item = item.parentNode;
        return item;
      }

      on(btn, 'click', function () {
        var item = frame();
        if (item) open(item);
      });

      /* The overlay image is lazy, so the first open would otherwise wait on
         the fetch. Warming it on hover makes the lightbox feel instant without
         costing anyone who never opens it. */
      on(btn, 'mouseenter', function () {
        var item = frame();
        if (!item || item.dataset.zoomWarmed) return;
        item.dataset.zoomWarmed = '1';
        new Image().src = item.dataset.zoomSrc;
      }, { passive: true });
    });

    /* A full-size look at the master, which is larger than any viewport. */
    on(image, 'click', function () {
      lightbox.classList.toggle('is-zoomed');
    });

    Array.prototype.forEach.call(
      lightbox.querySelectorAll('[data-lightbox-close]'),
      function (btn) { on(btn, 'click', close); }
    );
    on(prevBtn, 'click', function () { show(current - 1); });
    on(nextBtn, 'click', function () { show(current + 1); });

    on(document, 'keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(current - 1);
      else if (e.key === 'ArrowRight') show(current + 1);
    });

    /* Keeps tabbing from wandering onto the page behind the overlay. The
       three controls inside are the only places focus can legitimately go. */
    on(document, 'focusin', function (e) {
      if (!lightbox.hidden && !lightbox.contains(e.target) && closeBtn) closeBtn.focus();
    });
  }

  /* -- Boot ---------------------------------------------------------------- */

  function init(scope) {
    revealImages(scope);
    initNewsletter(scope);
    initCollection(scope);
    initPriceRange(scope);
    initProduct(scope);
    initProductGallery(scope);
    initQuantity(scope);
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-header]'),
      initHeader
    );
  }

  init(document);
  initParallax();
  initCart();

  /* Theme editor re-renders a section's markup in place; re-bind what landed. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    initParallax();
  });
})();
